import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactFlow, {
  Background, Controls, BackgroundVariant,
  type Node, type Edge, type ReactFlowInstance,
  Handle, Position, type NodeProps,
} from 'reactflow';
import 'reactflow/dist/style.css';
import html2canvas from 'html2canvas';
import { Copy, Check, ShieldAlert, ShieldCheck, Search, Waypoints, Download, Loader2, MousePointerClick } from 'lucide-react';
import { walletIntelApi } from '@/api/walletIntel';
import type { ClusterResult, ClusterNode as ClusterNodeData } from '@/types/walletIntel';
import { CATEGORY_LABEL, CATEGORY_COLOR } from '@/types/walletIntel';

function shorten(addr: string): string {
  if (addr.length <= 14) return addr;
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

function formatAmount(n: number | null): string {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(2)}K`;
  return n.toFixed(4);
}

// Node click is wired two independent ways: ReactFlow's own onNodeClick, AND
// a plain onClick baked into the node's own div via data.onSelect. Belt and
// braces — if one path is ever swallowed by a drag-threshold/event-bubbling
// quirk, the other still fires.
function WalletNode({ data, selected }: NodeProps) {
  const isSeed = data.hop === 0;
  const c = isSeed ? '#00C8FF' : data.flagged ? '#FF1F1F' : '#00C853';
  return (
    <div
      onClick={() => data.onSelect?.(data.address)}
      title={`${data.address} — click for details`}
      style={{
        minWidth: isSeed ? 72 : 60,
        padding: isSeed ? '10px 14px' : '8px 11px',
        borderRadius: 12,
        background: `${c}18`,
        border: `${selected ? 3 : isSeed ? 2 : 1.5}px solid ${selected ? '#DCE8F5' : c}`,
        boxShadow: `0 0 ${isSeed ? 20 : 12}px ${c}30`,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        cursor: 'pointer', userSelect: 'none',
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <span style={{ color: c, fontFamily: 'monospace', fontWeight: 800, fontSize: isSeed ? 11.5 : 10.5 }}>
        {isSeed ? 'SEED' : data.flagged ? 'FLAGGED' : 'CLEAR'}
      </span>
      <span style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 10, marginTop: 3 }}>
        {shorten(data.address)}
      </span>
    </div>
  );
}

const NODE_TYPES = { wallet: WalletNode };

type GraphNode = ClusterNodeData & { x: number; y: number };
type GraphEdge = { from: string; to: string; cumulative_amount: number | null };

function ringLayout(byAddress: Map<string, GraphNode>): Record<string, { x: number; y: number }> {
  const byHop: Record<number, GraphNode[]> = {};
  for (const n of byAddress.values()) (byHop[n.hop] = byHop[n.hop] ?? []).push(n);
  const maxHop = Math.max(...Object.keys(byHop).map(Number));
  const RING_STEP = 170;
  const pos: Record<string, { x: number; y: number }> = {};
  for (let hop = 0; hop <= maxHop; hop++) {
    const ring = byHop[hop] ?? [];
    const radius = hop * RING_STEP;
    ring.forEach((n, i) => {
      const angle = ring.length === 1 ? -Math.PI / 2 : (2 * Math.PI * i) / ring.length - Math.PI / 2;
      pos[n.address] = { x: radius * Math.cos(angle), y: radius * Math.sin(angle) };
    });
  }
  return pos;
}

function CopyableAddr({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(address); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
      className="flex items-center gap-1.5"
      style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', textAlign: 'left' }}
    >
      {address}
      {copied ? <Check size={12} style={{ color: '#00C853', flexShrink: 0 }} /> : <Copy size={12} style={{ color: '#5A7A9A', flexShrink: 0 }} />}
    </button>
  );
}

interface Props {
  cluster: ClusterResult;
  chain: string;
}

export function ClusterGraph({ cluster, chain }: Props) {
  const navigate = useNavigate();
  const wrapRef = useRef<HTMLDivElement>(null);

  // Stateful graph — starts from the initial /cluster result, grows in place
  // as the analyst expands nodes outward. Keyed by address so re-fetching a
  // node already on the graph just merges/no-ops rather than duplicating.
  const [nodesByAddr, setNodesByAddr] = useState<Map<string, GraphNode>>(() => {
    const m = new Map<string, GraphNode>();
    cluster.nodes.forEach((n) => m.set(n.address, { ...n, x: 0, y: 0 }));
    return m;
  });
  const [edgeList, setEdgeList] = useState<GraphEdge[]>(cluster.edges);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expanding, setExpanding] = useState<string | null>(null);
  const [expandError, setExpandError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const positions = useMemo(() => ringLayout(nodesByAddr), [nodesByAddr]);

  const onSelect = useCallback((address: string) => setSelectedId(address), []);

  const { nodes, edges } = useMemo(() => {
    const nodes: Node[] = Array.from(nodesByAddr.values()).map((n) => ({
      id: n.address,
      type: 'wallet',
      position: positions[n.address] ?? { x: 0, y: 0 },
      data: { address: n.address, hop: n.hop, flagged: n.flagged, matches: n.matches ?? [], onSelect },
      draggable: true,
      selected: n.address === selectedId,
    }));
    const edges: Edge[] = edgeList.map((e, i) => ({
      id: `e-${i}-${e.from}-${e.to}`,
      source: e.from,
      target: e.to,
      animated: true,
      label: formatAmount(e.cumulative_amount),
      labelStyle: { fill: '#8D96AC', fontFamily: 'monospace', fontSize: 10, fontWeight: 600 },
      labelBgStyle: { fill: '#0C1118', fillOpacity: 0.85 },
      style: { stroke: '#2979FF', strokeWidth: 1.3, strokeOpacity: 0.55 },
    }));
    return { nodes, edges };
  }, [nodesByAddr, edgeList, positions, selectedId, onSelect]);

  const onInit = useCallback((instance: ReactFlowInstance) => {
    instance.fitView({ padding: 0.3 });
  }, []);

  const selectedNode = selectedId ? nodesByAddr.get(selectedId) : undefined;
  const connectedEdges = selectedId ? edgeList.filter((e) => e.from === selectedId || e.to === selectedId) : [];

  async function expandNode(address: string) {
    setExpanding(address);
    setExpandError(null);
    try {
      const result = await walletIntelApi.cluster({ chain, address, max_hops: 1, max_fanout: 8 });
      setNodesByAddr((prev) => {
        const next = new Map(prev);
        const baseHop = next.get(address)?.hop ?? 0;
        for (const n of result.nodes) {
          if (n.address === address) continue; // that's the re-queried seed itself
          if (!next.has(n.address)) {
            next.set(n.address, { ...n, hop: baseHop + 1, x: 0, y: 0 });
          }
        }
        return next;
      });
      setEdgeList((prev) => {
        const seen = new Set(prev.map((e) => `${e.from}->${e.to}`));
        const merged = [...prev];
        for (const e of result.edges) {
          const key = `${e.from}->${e.to}`;
          if (!seen.has(key)) { merged.push(e); seen.add(key); }
        }
        return merged;
      });
    } catch (err) {
      setExpandError(err instanceof Error ? err.message : 'Failed to expand this node.');
    } finally {
      setExpanding(null);
    }
  }

  async function exportGraph() {
    if (!wrapRef.current) return;
    setExporting(true);
    try {
      const shot = await html2canvas(wrapRef.current, { backgroundColor: '#090D13', scale: 2, logging: false });
      const canvas = document.createElement('canvas');
      const headerH = 60, footerH = 34;
      canvas.width = shot.width;
      canvas.height = shot.height + headerH + footerH;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#050709';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#090D13';
      ctx.fillRect(0, 0, canvas.width, headerH);
      ctx.fillStyle = '#00C8FF';
      ctx.font = 'bold 22px monospace';
      ctx.textAlign = 'left';
      ctx.fillText('SYCEK', 24, 38);
      ctx.fillStyle = '#8D96AC';
      ctx.font = '14px monospace';
      ctx.fillText('Wallet Intelligence — Relationship Graph', 130, 38);
      ctx.textAlign = 'right';
      ctx.fillText(`Generated ${new Date().toLocaleString()}`, canvas.width - 24, 38);
      ctx.drawImage(shot, 0, headerH);
      ctx.fillStyle = '#090D13';
      ctx.fillRect(0, headerH + shot.height, canvas.width, footerH);
      ctx.fillStyle = '#3A5A7A';
      ctx.font = '11px monospace';
      ctx.textAlign = 'center';
      ctx.fillText('Generated by Sycek OSINT — sycek.io — for internal investigative use', canvas.width / 2, headerH + shot.height + 22);

      const link = document.createElement('a');
      link.download = `sycek-wallet-relationships-${cluster.seed_address.slice(0, 10)}-${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      link.click();
    } catch {
      setExpandError('Failed to export the graph as an image.');
    } finally {
      setExporting(false);
    }
  }

  if (nodesByAddr.size <= 1) {
    return (
      <div style={{ height: 380, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#5A7A9A', fontSize: 13 }}>
          No counterparty data found for this address.
        </p>
      </div>
    );
  }

  return (
    <div style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ padding: '11px 18px', borderBottom: '1px solid #182030', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <span className="flex items-center gap-1.5" style={{ color: '#8D96AC', fontSize: 11.5, fontWeight: 600 }}>
          <MousePointerClick size={13} /> CLICK A NODE FOR DETAILS · EXPAND TO TRACE FURTHER
        </span>
        <div className="flex items-center gap-3">
          <span style={{ color: '#5A7A9A', fontSize: 11 }}>
            {nodesByAddr.size} nodes · {edgeList.length} edges
          </span>
          <motion.button
            whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
            onClick={exportGraph}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md font-bold tracking-wide"
            style={{ background: '#0C1118', color: '#8D96AC', border: '1px solid #182030', fontSize: 11 }}
          >
            {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
            EXPORT PNG
          </motion.button>
        </div>
      </div>
      <div style={{ height: 460, display: 'flex' }}>
        <div ref={wrapRef} style={{ flex: 1, minWidth: 0 }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            onInit={onInit}
            onNodeClick={(_, node) => setSelectedId(node.id)}
            onPaneClick={() => setSelectedId(null)}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            proOptions={{ hideAttribution: true }}
            minZoom={0.3}
            maxZoom={2}
            style={{ background: '#090D13' }}
          >
            <Background color="#182030" gap={20} size={1} variant={BackgroundVariant.Dots} />
            <Controls style={{ background: '#0C1118', border: '1px solid #182030' }} showInteractive={false} />
          </ReactFlow>
        </div>

        {/* Inspector panel */}
        <div style={{ width: 270, flexShrink: 0, borderLeft: '1px solid #182030', background: '#0C1118', overflowY: 'auto', padding: 16 }}>
          {!selectedNode ? (
            <div className="flex flex-col items-center text-center gap-3 pt-6">
              <Waypoints size={24} style={{ color: '#3A5A7A' }} />
              <p style={{ color: '#5A7A9A', fontSize: 12.5, lineHeight: 1.6 }}>
                Click any wallet in the graph for its full address, flag status, and connected transfers.
                From there, expand it to trace the money one hop further, or open its own full profile.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                {selectedNode.flagged
                  ? <ShieldAlert size={16} style={{ color: '#FF1F1F' }} />
                  : <ShieldCheck size={16} style={{ color: selectedNode.hop === 0 ? '#00C8FF' : '#00C853' }} />}
                <span style={{ color: '#F2F6FC', fontWeight: 700, fontSize: 13.5 }}>
                  {selectedNode.hop === 0 ? 'Seed address' : selectedNode.flagged ? 'Flagged' : 'Clear'}
                </span>
              </div>
              <CopyableAddr address={selectedNode.address} />
              <div style={{ color: '#7C8CA6', fontSize: 11.5 }}>Hop {selectedNode.hop} from seed</div>

              {(selectedNode.matches?.length ?? 0) > 0 && (
                <div className="flex flex-col gap-2 mt-1">
                  <p style={{ color: '#7C8CA6', fontSize: 11, letterSpacing: '0.06em', fontWeight: 700 }}>CITATIONS</p>
                  {selectedNode.matches!.map((m, i) => (
                    <div key={i} style={{ background: '#050709', border: '1px solid #182030', borderRadius: 9, padding: '9px 11px' }}>
                      <span
                        style={{ background: `${CATEGORY_COLOR[m.category] ?? '#5A7A9A'}18`, color: CATEGORY_COLOR[m.category] ?? '#5A7A9A', fontSize: 10 }}
                        className="font-bold px-2.5 py-1 rounded-full uppercase"
                      >
                        {CATEGORY_LABEL[m.category] ?? m.category}
                      </span>
                      <p style={{ color: '#8D96AC', fontSize: 11.5, marginTop: 6, lineHeight: 1.45 }}>{m.citation}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-col gap-1.5 mt-1">
                <p style={{ color: '#7C8CA6', fontSize: 11, letterSpacing: '0.06em', fontWeight: 700 }}>
                  CONNECTED TRANSFERS ({connectedEdges.length})
                </p>
                {connectedEdges.map((e, i) => {
                  const outgoing = e.from === selectedId;
                  const other = outgoing ? e.to : e.from;
                  return (
                    <div key={i} style={{ background: '#050709', border: '1px solid #182030', borderRadius: 8, padding: '8px 10px' }}
                      className="flex items-center justify-between gap-2">
                      <span style={{ color: '#8D96AC', fontFamily: 'monospace', fontSize: 11 }}>
                        {outgoing ? '→' : '←'} {shorten(other)}
                      </span>
                      <span style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>
                        {formatAmount(e.cumulative_amount)}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-col gap-2 mt-2">
                <motion.button
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  onClick={() => expandNode(selectedNode.address)}
                  disabled={expanding === selectedNode.address}
                  className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg font-bold tracking-wide"
                  style={{ background: '#0F2030', color: '#00C8FF', border: '1px solid #1E3A5F', fontSize: 12 }}
                >
                  {expanding === selectedNode.address ? <Loader2 size={13} className="animate-spin" /> : <Waypoints size={13} />}
                  TRACE THIS WALLET FURTHER
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  onClick={() => navigate(`/app/wallet-intel/lookup?address=${encodeURIComponent(selectedNode.address)}&chain=${chain}`)}
                  className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg font-bold tracking-wide"
                  style={{ background: '#0C1118', color: '#8D96AC', border: '1px solid #182030', fontSize: 12 }}
                >
                  <Search size={13} /> OPEN FULL PROFILE
                </motion.button>
              </div>
              {expandError && <p style={{ color: '#FF1F1F', fontSize: 11 }}>{expandError}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
